import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useOrganizationStore } from '../stores/organization.store';
import { useAuthStore } from '../stores/auth.store';
import { OrganizationSchema, OrganizationType, getOrganizationTypeLabel } from '../types/organization.type';
import { useTranslation } from 'react-i18next';

const Organizations: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user, isPlatformAdmin } = useAuthStore();
  const legacyPerms = user?.data?.permissions;
  const isLegacyAdmin =
    legacyPerms &&
    !Array.isArray(legacyPerms) &&
    Object.keys(legacyPerms).includes('*') &&
    legacyPerms['*']?.includes('admin');
  const isAdmin = user?.username === 'anas' || isPlatformAdmin || !!isLegacyAdmin;
  const {
    organizations,
    getOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
  } = useOrganizationStore();

  const [open, setOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationSchema | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    organization_type: OrganizationType.RECYCLER,
    google_maps_url: '',
    data: {
      email: '',
      phone_number: '',
      industry: [] as string[],
      more_info: '',
    },
  });

  // Industry options based on organization type
  const industryOptions = {
    [OrganizationType.RECYCLER]: [
      'Paper', 'Plastic', 'Metal', 'E-waste', 'Glass', 'Organic', 'Other'
    ],
    [OrganizationType.TRUCK]: [
      'General Hauling', 'Recycling Transport', 'Hazardous Waste', 'Bulk Waste', 'Other'
    ],
    [OrganizationType.WASTE_OUTLET]: [
      'Landfill', 'Compost', 'Incinerator', 'Transfer Station', 'Other'
    ],
  };

  useEffect(() => {
    console.log('User:', user);
    console.log('Is Admin:', isAdmin);
    getOrganizations();
  }, []);

  const handleOpen = (org?: OrganizationSchema) => {
    if (org) {
      setSelectedOrg(org);
      setFormData({
        name: org.name,
        organization_type: org.organization_type,
        google_maps_url: org.google_maps_url,
        data: {
          email: org.data?.email || '',
          phone_number: org.data?.phone_number || '',
          industry: org.data?.industry || [],
          more_info: org.data?.more_info || '',
        },
      });
    } else {
      setSelectedOrg(null);
      setFormData({
        name: '',
        organization_type: OrganizationType.RECYCLER,
        google_maps_url: '',
        data: {
          email: '',
          phone_number: '',
          industry: [],
          more_info: '',
        },
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedOrg(null);
    setFormData({
      name: '',
      organization_type: OrganizationType.RECYCLER,
      google_maps_url: '',
      data: {
        email: '',
        phone_number: '',
        industry: [],
        more_info: '',
      },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedOrg) {
      await updateOrganization(selectedOrg.id, formData);
    } else {
      await createOrganization(formData);
    }
    handleClose();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('organizations.confirmDelete'))) {
      await deleteOrganization(id);
    }
  };

  const getOrganizationTypeColor = (type: OrganizationType) => {
    switch (type) {
      case OrganizationType.RECYCLER:
        return theme.palette.success.main;
      case OrganizationType.WASTE_OUTLET:
        return theme.palette.primary.main;
      case OrganizationType.TRUCK:
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        p: 3,
        borderRadius: "16px",
        bgcolor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
      }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 600,
          color: theme.palette.primary.main,
        }}>
          Organizations
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              bgcolor: '#0000fe',
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                bgcolor: '#0000cc',
                boxShadow: '0 4px 20px rgba(0, 0, 254, 0.4)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            {t('organizations.add')}
          </Button>
        )}
      </Box>

      <Paper 
        sx={{ 
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: theme.shadows[2],
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('organizations.columns.name')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('organizations.columns.type')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('organizations.columns.contact')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('organizations.columns.location')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t('organizations.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizations.map((org) => (
                <TableRow 
                  key={org.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: theme.palette.action.hover,
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {org.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getOrganizationTypeLabel(org.organization_type)}
                      sx={{ 
                        bgcolor: getOrganizationTypeColor(org.organization_type),
                        color: theme.palette.common.white,
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {org.data?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ color: theme.palette.text.secondary }} />
                      <Typography variant="body2" color="text.secondary">
                        {org.google_maps_url}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={() => handleOpen(org)}
                      sx={{ 
                        color: theme.palette.primary.main,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    {isAdmin && (
                      <IconButton 
                        onClick={() => handleDelete(org.id)}
                        sx={{ 
                          color: theme.palette.error.main,
                          '&:hover': { 
                            bgcolor: theme.palette.error.light,
                            color: theme.palette.error.contrastText,
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            borderRadius: "16px",
          }
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: "16px 16px 0 0",
          }}>
            {selectedOrg ? t('organizations.edit') : t('organizations.add')}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isAdmin ? (
                <>
                  <TextField
                    label={t('organizations.form.name')}
                    fullWidth
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>{t('organizations.form.type')}</InputLabel>
                    <Select
                      value={formData.organization_type}
                      onChange={(e) => setFormData({ ...formData, organization_type: e.target.value as OrganizationType })}
                      required
                      label={t('organizations.form.type')}
                      sx={{
                        borderRadius: '8px',
                      }}
                    >
                      <MenuItem value={OrganizationType.RECYCLER}>{t('organizations.form.recycler')}</MenuItem>
                      <MenuItem value={OrganizationType.WASTE_OUTLET}>{t('organizations.form.wasteOutlet')}</MenuItem>
                      <MenuItem value={OrganizationType.TRUCK}>{t('organizations.form.truck')}</MenuItem>
                    </Select>
                  </FormControl>
                </>
              ) : (
                <>
                  <TextField
                    label={t('organizations.form.name')}
                    fullWidth
                    value={formData.name}
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>{t('organizations.form.type')}</InputLabel>
                    <Select
                      value={formData.organization_type}
                      disabled
                      label={t('organizations.form.type')}
                      sx={{
                        borderRadius: '8px',
                      }}
                    >
                      <MenuItem value={formData.organization_type}>
                        {getOrganizationTypeLabel(formData.organization_type)}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
              <TextField
                label={t('organizations.form.contactEmail')}
                fullWidth
                value={formData.data.email}
                onChange={(e) => setFormData({ ...formData, data: { ...formData.data, email: e.target.value } })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <TextField
                label={t('organizations.form.phoneNumber')}
                fullWidth
                value={formData.data.phone_number}
                onChange={(e) => setFormData({ ...formData, data: { ...formData.data, phone_number: e.target.value } })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <FormControl fullWidth>
                <InputLabel>{t('organizations.form.industry')}</InputLabel>
                <Select
                  multiple
                  value={formData.data.industry}
                  onChange={(e) => setFormData({ ...formData, data: { ...formData.data, industry: e.target.value as string[] } })}
                  required
                  label={t('organizations.form.industry')}
                  sx={{
                    borderRadius: '8px',
                  }}
                >
                  {(industryOptions[formData.organization_type] || []).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('organizations.form.industryHelper')}</FormHelperText>
              </FormControl>
              <TextField
                label={t('organizations.form.googleMapsUrl')}
                fullWidth
                value={formData.google_maps_url}
                onChange={(e) => setFormData({ ...formData, google_maps_url: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <TextField
                label={t('organizations.form.moreInfo')}
                fullWidth
                multiline
                rows={3}
                value={formData.data.more_info}
                onChange={(e) => setFormData({ ...formData, data: { ...formData.data, more_info: e.target.value } })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                bgcolor: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                },
                borderRadius: '8px',
                px: 3,
              }}
            >
              {selectedOrg ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Organizations; 